import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatNumber } from '@/utils/number';
import type { RequisitionSheetInstitute } from '../types/productRequisition.types';
import {
  QTY_FIELDS,
  requisitionStageLabel,
  toDraftLine,
  type SheetDraft,
  type SheetDraftLine,
} from '../utils/sheetDrafts';

interface RequisitionSheetTableProps {
  institutes: RequisitionSheetInstitute[];
  draft: SheetDraft;
  onChange: (instituteId: number, line: SheetDraftLine) => void;
}

export function RequisitionSheetTable({ institutes, draft, onChange }: RequisitionSheetTableProps) {
  return (
    <div className="overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">SL</TableHead>
            <TableHead className="min-w-56">Institute</TableHead>
            <TableHead>Thana</TableHead>
            {QTY_FIELDS.map((field) => (
              <TableHead key={field.key} className="w-32">
                {field.label}
              </TableHead>
            ))}
            <TableHead className="w-40">Target Date</TableHead>
            <TableHead>Stage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {institutes.map((row, index) => {
            const line = draft[row.instituteId] ?? toDraftLine(row);
            const isLocked = row.isReview;

            return (
              <TableRow key={row.instituteId}>
                <TableCell className="tabular-nums">{index + 1}</TableCell>
                <TableCell>
                  <div className="font-medium">{row.instituteName}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.instituteTypeName ?? EMPTY_VALUE}
                  </div>
                </TableCell>
                <TableCell>{row.thanaName ?? EMPTY_VALUE}</TableCell>

                {QTY_FIELDS.map((field) =>
                  isLocked ? (
                    <TableCell key={field.key} className="tabular-nums">
                      {formatNumber(row[field.key])}
                    </TableCell>
                  ) : (
                    <TableCell key={field.key}>
                      <Input
                        type="number"
                        min={0}
                        aria-label={`${field.label} for ${row.instituteName}`}
                        value={line[field.key]}
                        onChange={(event) =>
                          onChange(row.instituteId, {
                            ...line,
                            [field.key]: event.target.value,
                          })
                        }
                      />
                    </TableCell>
                  ),
                )}

                <TableCell>
                  {isLocked ? (
                    (row.targetDate ?? EMPTY_VALUE)
                  ) : (
                    <Input
                      type="date"
                      aria-label={`Target date for ${row.instituteName}`}
                      value={line.targetDate}
                      onChange={(event) =>
                        onChange(row.instituteId, { ...line, targetDate: event.target.value })
                      }
                    />
                  )}
                </TableCell>

                <TableCell>{requisitionStageLabel(row)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
